import { useEffect, useState, useCallback, useRef } from "react";
import { Toaster, toast } from "sonner";
import Login from "./components/app/Login";
import Shell from "./components/app/Shell";
import Dashboard from "./components/app/Dashboard";
import Activities from "./components/app/Activities";
import Orders from "./components/app/Orders";
import ClientOrders from "./components/app/ClientOrders";
import OrderDetail from "./components/app/OrderDetail";
import Clients from "./components/app/Clients";
import ClientDetail from "./components/app/ClientDetail";
import Leads from "./components/app/Leads";
import LeadDetail from "./components/app/LeadDetail";
import StageQueue from "./components/app/StageQueue";
import Production from "./components/app/Production";
import Design from "./components/app/Design";
import DesignDetail from "./components/app/DesignDetail";
import ProductionDetail from "./components/app/ProductionDetail";
import Dispatch from "./components/app/Dispatch";
import DispatchDetail from "./components/app/DispatchDetail";
import DeliveryDetail from "./components/app/DeliveryDetail";
import DeliveryPersonDetail from "./components/app/DeliveryPersonDetail";
import Delivery from "./components/app/Delivery";
import Approvals from "./components/app/Approvals";
import Reports from "./components/app/Reports";
import UsersRoles from "./components/app/UsersRoles";
import UserDetail from "./components/app/UserDetail";
import Settings from "./components/app/Settings";
import Profile from "./components/app/Profile";
import ApprovalPage from "./components/app/ApprovalPage";
import PayPage from "./components/app/PayPage";
import TasksReports from "./components/app/TasksReports";
import TaskDetail from "./components/app/TaskDetail";
import TaskReminderWidget from "./components/app/TaskReminderWidget";
import Procurement from "./components/app/Procurement";
import ProcurementDetail from "./components/app/ProcurementDetail";
import Accounts from "./components/app/Accounts";
import ClientAccounts from "./components/app/ClientAccounts";
import { api, getStoredUser, setAuthSession, clearAuthSession } from "./lib/api";
import { parseRoute, getPathForRoute } from "./lib/routes";
import { setListState } from "./lib/listState";
import { isAdmin, isSuperAdmin, isPageAllowedForUser, getDefaultPageForUser } from "./lib/permissions";
import PasswordInput from "./components/ui/PasswordInput";
import "@/App.css";

export default function App() {
  const [route, setRoute] = useState(() => parseRoute(window.location.pathname, window.location.search));

  useEffect(() => {
    const onPop = () => setRoute(parseRoute(window.location.pathname, window.location.search));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  if (route.isPublic && route.page === "ApprovalPage" && route.token) {
    return (<><ApprovalPage token={route.token} /><Toaster richColors position="top-right" /></>);
  }
  if (route.isPublic && route.page === "PayPage" && route.token) {
    return (<><PayPage token={route.token} /><Toaster richColors position="top-right" /></>);
  }
  return <AppInner />;
}

function AppInner() {
  // Optimistically restore session from localStorage to prevent screen flashes on refresh
  const storedUser = getStoredUser();
  const [user, setUser] = useState(storedUser || null);
  const [checking, setChecking] = useState(!storedUser);
  const [deactivationMsg, setDeactivationMsg] = useState("");

  // Initialize view state from current browser URL evaluating all assigned departments
  const initialRoute = parseRoute(window.location.pathname, window.location.search);
  const routePage = initialRoute.page || "Dashboard";
  const initialPage = isPageAllowedForUser(routePage, user)
    ? routePage
    : getDefaultPageForUser(user);

  const [page, setPage] = useState(initialPage);
  const [selectedOrderId, setSelectedOrderId] = useState(initialRoute.selectedOrderId || initialRoute.orderId || null);
  const [selectedDesignOrderId, setSelectedDesignOrderId] = useState(initialRoute.designOrderId || initialRoute.orderId || null);
  const [selectedProductionOrderId, setSelectedProductionOrderId] = useState(initialRoute.productionOrderId || initialRoute.orderId || null);
  const [selectedDispatchOrderId, setSelectedDispatchOrderId] = useState(initialRoute.dispatchOrderId || initialRoute.orderId || null);
  const [selectedDeliveryOrderId, setSelectedDeliveryOrderId] = useState(initialRoute.deliveryOrderId || initialRoute.orderId || null);
  const [selectedDeliveryPersonId, setSelectedDeliveryPersonId] = useState(initialRoute.selectedDeliveryPersonId || initialRoute.personId || null);
  const [selectedClientId, setSelectedClientId] = useState(initialRoute.selectedClientId || initialRoute.clientId || null);
  const [selectedLeadId, setSelectedLeadId] = useState(initialRoute.selectedLeadId || initialRoute.leadId || null);
  const [selectedUserId, setSelectedUserId] = useState(initialRoute.selectedUserId || initialRoute.userId || null);
  const [selectedTaskId, setSelectedTaskId] = useState(initialRoute.selectedTaskId || initialRoute.taskId || null);
  const [selectedRequestId, setSelectedRequestId] = useState(initialRoute.selectedRequestId || initialRoute.requestId || null);
  const [routeParams, setRouteParams] = useState(initialRoute);

  const currentRouteRef = useRef({
    page: initialPage,
    orderId: initialRoute.selectedOrderId || initialRoute.orderId || null,
    selectedOrderId: initialRoute.selectedOrderId || initialRoute.orderId || null,
    designOrderId: initialRoute.designOrderId || null,
    productionOrderId: initialRoute.productionOrderId || null,
    dispatchOrderId: initialRoute.dispatchOrderId || null,
    deliveryOrderId: initialRoute.deliveryOrderId || null,
    personId: initialRoute.selectedDeliveryPersonId || initialRoute.personId || null,
    selectedDeliveryPersonId: initialRoute.selectedDeliveryPersonId || initialRoute.personId || null,
    clientId: initialRoute.selectedClientId || initialRoute.clientId || null,
    selectedClientId: initialRoute.selectedClientId || initialRoute.clientId || null,
    leadId: initialRoute.selectedLeadId || initialRoute.leadId || null,
    selectedLeadId: initialRoute.selectedLeadId || initialRoute.leadId || null,
    userId: initialRoute.selectedUserId || initialRoute.userId || null,
    selectedUserId: initialRoute.selectedUserId || initialRoute.userId || null,
    taskId: initialRoute.selectedTaskId || initialRoute.taskId || null,
    selectedTaskId: initialRoute.selectedTaskId || initialRoute.taskId || null,
    requestId: initialRoute.selectedRequestId || initialRoute.requestId || null,
    selectedRequestId: initialRoute.selectedRequestId || initialRoute.requestId || null,
    tab: initialRoute.tab || null,
  });
  const navHistoryRef = useRef([]);

  const [dashboard, setDashboard] = useState(null);
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedDashboardCompany, setSelectedDashboardCompany] = useState("All Companies");
  const [companiesList, setCompaniesList] = useState([]);

  // Forced password change state
  const [changePwdForm, setChangePwdForm] = useState({ current: "", next: "", confirm: "" });
  const [changePwdError, setChangePwdError] = useState("");
  const [changePwdLoading, setChangePwdLoading] = useState(false);
  const changePwdRef = useRef(null);

  const loadCore = useCallback(async (comp = selectedDashboardCompany) => {
    try {
      const isAll = !comp || comp === "All Companies";
      const dashUrl = isAll ? "/dashboard" : `/dashboard?company=${encodeURIComponent(comp)}`;
      const ordersUrl = isAll ? "/orders" : `/orders?company=${encodeURIComponent(comp)}`;

      const [d, o, c] = await Promise.all([
        api("get", dashUrl),
        api("get", ordersUrl),
        api("get", "/clients"),
      ]);
      setDashboard(d.data);
      setOrders(o.data);
      setClients(c.data);
    } catch (e) {
      if (e.response?.status === 401) {
        clearAuthSession();
        setUser(false);
      } else if (e.response?.status === 403) {
        const detail = e.response?.data?.detail || "";
        if (detail.toLowerCase().includes("deactivated")) {
          clearAuthSession();
          setDeactivationMsg(detail);
          setUser(false);
        }
      }
    }
  }, [selectedDashboardCompany]);

  const handleDashboardCompanyChange = useCallback(async (compName) => {
    setSelectedDashboardCompany(compName);
    try {
      const isAll = !compName || compName === "All Companies";
      const dashUrl = isAll ? "/dashboard" : `/dashboard?company=${encodeURIComponent(compName)}`;
      const ordersUrl = isAll ? "/orders" : `/orders?company=${encodeURIComponent(compName)}`;

      const [d, o] = await Promise.all([
        api("get", dashUrl),
        api("get", ordersUrl),
      ]);
      setDashboard(d.data);
      setOrders(o.data);
    } catch (err) {
      console.error("Failed to load company dashboard", err);
    }
  }, []);

  useEffect(() => {
    api("get", "/auth/me")
      .then((r) => {
        const u = r.data;
        setUser(u);
        setAuthSession(null, u);
        loadCore();
        if (isSuperAdmin(u)) {
          api("get", "/companies?active_only=true")
            .then((res) => {
              if (Array.isArray(res.data)) {
                setCompaniesList(res.data);
              }
            })
            .catch(() => {});
        }
        const current = parseRoute(window.location.pathname, window.location.search);
        if (!isPageAllowedForUser(current.page, u)) {
          navigateTo(getDefaultPageForUser(u), { replace: true });
        }
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          clearAuthSession();
          setUser(false);
        } else if (err.response?.status === 403) {
          const detail = err.response?.data?.detail || "";
          if (detail.toLowerCase().includes("deactivated")) {
            clearAuthSession();
            setDeactivationMsg(detail);
            setUser(false);
          } else {
            clearAuthSession();
            setUser(false);
          }
        }
      })
      .finally(() => setChecking(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadCore]);

  // Central navigation handler updating browser URL & history
  const navigateTo = useCallback((target, { replace = false, isBack = false } = {}) => {
    let pageName;
    let orderId = null;
    let designOrderId = null;
    let productionOrderId = null;
    let dispatchOrderId = null;
    let deliveryOrderId = null;
    let deliveryPersonId = null;
    let navDeliveryPersonId = null;
    let clientId = null;
    let navClientId = null;
    let leadId = null;
    let navLeadId = null;
    let userId = null;
    let navUserId = null;
    let taskId = null;
    let navTaskId = null;
    let requestId = null;
    let navRequestId = null;
    let tab = null;
    let stage = null;
    let statusFilter = null;
    let dateFilter = null;
    let filter = null;
    let month = null;
    let company = null;
    let activeTab = null;

    if (typeof target === "string") {
      pageName = target;
    } else if (target && typeof target === "object") {
      pageName = target.page;
      orderId = target.orderId || target.selectedOrderId || null;
      designOrderId = target.designOrderId || null;
      productionOrderId = target.productionOrderId || null;
      dispatchOrderId = target.dispatchOrderId || null;
      deliveryOrderId = target.deliveryOrderId || null;
      deliveryPersonId = target.personId || target.deliveryPersonId || null;
      navDeliveryPersonId = target.selectedDeliveryPersonId || target.personId || null;
      clientId = target.clientId || null;
      navClientId = target.selectedClientId || null;
      leadId = target.leadId || null;
      navLeadId = target.selectedLeadId || null;
      userId = target.userId || null;
      navUserId = target.selectedUserId || null;
      taskId = target.taskId || null;
      navTaskId = target.selectedTaskId || null;
      requestId = target.requestId || null;
      navRequestId = target.selectedRequestId || null;
      tab = target.tab || null;

      stage = target.stage || null;
      statusFilter = target.statusFilter || target.status || null;
      dateFilter = target.dateFilter || target.timeframe || null;
      filter = target.filter || null;
      month = target.month || null;
      company = target.company || null;
      activeTab = target.activeTab || null;
    }

    if (!isPageAllowedForUser(pageName, user)) {
      pageName = getDefaultPageForUser(user);
    }

    const nextRouteState = {
      page: pageName,
      orderId,
      selectedOrderId: orderId,
      designOrderId,
      productionOrderId,
      dispatchOrderId,
      deliveryOrderId,
      personId: deliveryPersonId,
      selectedDeliveryPersonId: navDeliveryPersonId || deliveryPersonId,
      clientId,
      selectedClientId: navClientId || clientId,
      leadId,
      selectedLeadId: navLeadId || leadId,
      userId,
      selectedUserId: navUserId || userId,
      taskId,
      selectedTaskId: navTaskId || taskId,
      requestId,
      selectedRequestId: navRequestId || requestId,
      tab,
      stage,
      statusFilter,
      dateFilter,
      filter,
      month,
      company,
      activeTab,
    };

    // Synchronize to listState for instant persistence
    if (pageName === "Orders") {
      setListState("orders", (prev) => ({
        ...prev,
        stage: stage !== null ? stage : (prev?.stage || ""),
        filter: filter !== null ? filter : "",
        month: month !== null ? month : "",
        company: company !== null ? company : "",
      }));
    } else if (pageName === "Dispatch") {
      setListState("dispatch_main", (prev) => ({
        ...prev,
        statusFilter: statusFilter !== null ? statusFilter : (prev?.statusFilter || ""),
      }));
    } else if (pageName === "Delivery") {
      setListState("delivery_main", (prev) => ({
        ...prev,
        statusFilter: statusFilter !== null ? statusFilter : (prev?.statusFilter || ""),
        dateFilter: dateFilter !== null ? dateFilter : (prev?.dateFilter || "all"),
      }));
    } else if (pageName === "Approvals") {
      setListState("approvals", (prev) => ({
        ...prev,
        activeTab: activeTab || tab || "pending",
      }));
    } else if (pageName === "Production") {
      setListState("production", (prev) => ({
        ...prev,
        tab: tab || "orders",
      }));
    }

    const targetUrl = getPathForRoute(nextRouteState);
    const currentFull = window.location.pathname + window.location.search;

    if (!replace && !isBack && currentRouteRef.current) {
      const prevUrl = getPathForRoute(currentRouteRef.current);
      if (prevUrl !== targetUrl) {
        navHistoryRef.current.push({ ...currentRouteRef.current });
      }
    }

    currentRouteRef.current = nextRouteState;

    if (currentFull !== targetUrl) {
      if (replace) {
        window.history.replaceState(nextRouteState, "", targetUrl);
      } else {
        window.history.pushState(nextRouteState, "", targetUrl);
      }
    }

    setPage(pageName);
    setRouteParams(nextRouteState);
    setSelectedOrderId(orderId);
    setSelectedDesignOrderId(designOrderId || orderId);
    setSelectedProductionOrderId(productionOrderId || orderId);
    setSelectedDispatchOrderId(dispatchOrderId || orderId);
    setSelectedDeliveryOrderId(deliveryOrderId || orderId);
    setSelectedDeliveryPersonId(navDeliveryPersonId || deliveryPersonId);
    setSelectedClientId(navClientId || clientId);
    setSelectedLeadId(navLeadId || leadId);
    setSelectedUserId(navUserId || userId);
    setSelectedTaskId(navTaskId || taskId);
    setSelectedRequestId(navRequestId || requestId);
    loadCore();
  }, [loadCore, user]);

  // Handle browser Back & Forward navigation (popstate)
  useEffect(() => {
    const handlePopState = () => {
      const route = parseRoute(window.location.pathname, window.location.search);
      let targetPage = route.page;
      if (!isPageAllowedForUser(targetPage, user)) {
        targetPage = getDefaultPageForUser(user);
      }

      const poppedState = {
        page: targetPage,
        orderId: route.selectedOrderId || route.orderId || null,
        selectedOrderId: route.selectedOrderId || route.orderId || null,
        designOrderId: route.designOrderId || route.orderId || null,
        productionOrderId: route.productionOrderId || route.orderId || null,
        dispatchOrderId: route.dispatchOrderId || route.orderId || null,
        deliveryOrderId: route.deliveryOrderId || route.orderId || null,
        personId: route.selectedDeliveryPersonId || route.personId || null,
        selectedDeliveryPersonId: route.selectedDeliveryPersonId || route.personId || null,
        clientId: route.selectedClientId || route.clientId || null,
        selectedClientId: route.selectedClientId || route.clientId || null,
        leadId: route.selectedLeadId || route.leadId || null,
        selectedLeadId: route.selectedLeadId || route.leadId || null,
        userId: route.selectedUserId || route.userId || null,
        selectedUserId: route.selectedUserId || route.userId || null,
        taskId: route.selectedTaskId || route.taskId || null,
        selectedTaskId: route.selectedTaskId || route.taskId || null,
        requestId: route.selectedRequestId || route.requestId || null,
        selectedRequestId: route.selectedRequestId || route.requestId || null,
        tab: route.tab || null,
        stage: route.stage || null,
        statusFilter: route.statusFilter || null,
        dateFilter: route.dateFilter || null,
        filter: route.filter || null,
        month: route.month || null,
        company: route.company || null,
        activeTab: route.activeTab || null,
      };

      if (navHistoryRef.current.length > 0) {
        navHistoryRef.current.pop();
      }
      currentRouteRef.current = poppedState;

      setPage(targetPage);
      setRouteParams(poppedState);
      setSelectedOrderId(route.selectedOrderId || route.orderId || null);
      setSelectedDesignOrderId(route.designOrderId || route.orderId || null);
      setSelectedProductionOrderId(route.productionOrderId || route.orderId || null);
      setSelectedDispatchOrderId(route.dispatchOrderId || route.orderId || null);
      setSelectedDeliveryOrderId(route.deliveryOrderId || route.orderId || null);
      setSelectedDeliveryPersonId(route.selectedDeliveryPersonId || route.personId || null);
      setSelectedClientId(route.selectedClientId || route.clientId || null);
      setSelectedLeadId(route.selectedLeadId || route.leadId || null);
      setSelectedUserId(route.selectedUserId || route.userId || null);
      setSelectedTaskId(route.selectedTaskId || route.taskId || null);
      setSelectedRequestId(route.selectedRequestId || route.requestId || null);
      loadCore();
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [loadCore, user]);

  // Clean canonical URL on initial mount if invalid path or "/"
  useEffect(() => {
    const current = parseRoute(window.location.pathname, window.location.search);
    if (current.isNotFound) {
      navigateTo(getDefaultPageForUser(user), { replace: true });
    }
  }, [navigateTo, user]);

  const goBack = useCallback((fallback = "Orders") => {
    if (navHistoryRef.current.length > 0) {
      const prev = navHistoryRef.current.pop();
      if (prev && prev.page) {
        navigateTo(prev, { replace: false, isBack: true });
        return;
      }
    }
    if (fallback) {
      navigateTo(fallback, { replace: false, isBack: true });
    } else {
      navigateTo("Orders", { replace: false, isBack: true });
    }
  }, [navigateTo]);

  const openClientOrders = (clientId) => {
    navigateTo({ page: "ClientOrders", selectedClientId: clientId, clientId });
  };

  const openDeliveryPerson = (personId) => {
    navigateTo({ page: "DeliveryPersonDetail", selectedDeliveryPersonId: personId, personId });
  };

  const openOrder = (orderId, tab) => {
    navigateTo({ page: "OrderDetail", orderId, selectedOrderId: orderId, tab });
  };

  const openDesign = (orderId) => {
    navigateTo({ page: "DesignDetail", designOrderId: orderId, orderId, selectedOrderId: orderId });
  };

  const openProduction = (orderId) => {
    navigateTo({ page: "ProductionDetail", productionOrderId: orderId, orderId, selectedOrderId: orderId });
  };

  const openDispatch = (orderId) => {
    navigateTo({ page: "DispatchDetail", dispatchOrderId: orderId, orderId, selectedOrderId: orderId });
  };

  const openDelivery = (orderId) => {
    navigateTo({ page: "DeliveryDetail", deliveryOrderId: orderId, orderId, selectedOrderId: orderId });
  };

  const openClient = (clientId) => {
    navigateTo({ page: "ClientDetail", selectedClientId: clientId, clientId });
  };

  const openLead = (leadId) => {
    navigateTo({ page: "LeadDetail", selectedLeadId: leadId, leadId });
  };

  const openUser = (userId) => {
    navigateTo({ page: "UserDetail", selectedUserId: userId, userId });
  };

  const openTask = (taskId) => {
    navigateTo({ page: "TaskDetail", selectedTaskId: taskId, taskId });
  };

  const openProcurement = (requestId) => {
    navigateTo({ page: "ProcurementDetail", selectedRequestId: requestId, requestId });
  };

  const openClientAccounts = (clientId) => {
    navigateTo({ page: "ClientAccounts", selectedClientId: clientId, clientId });
  };

  const goto = (nextPage) => {
    navigateTo(nextPage);
  };

  const logout = async () => {
    await api("post", "/auth/logout").catch(() => {});
    clearAuthSession();
    setUser(false);
    navigateTo("Dashboard", { replace: true });
  };

  const handleForcedPasswordChange = async (e) => {
    e.preventDefault();
    setChangePwdError("");
    if (changePwdForm.next !== changePwdForm.confirm) {
      setChangePwdError("New passwords do not match.");
      return;
    }
    if (changePwdForm.next.length < 6) {
      setChangePwdError("New password must be at least 6 characters.");
      return;
    }
    setChangePwdLoading(true);
    try {
      const res = await api("post", "/auth/change-password", {
        current_password: changePwdForm.current,
        new_password: changePwdForm.next,
      });
      setUser((u) => {
        const nextUser = { ...(u || {}), ...(res.data?.user || {}), must_change_password: false };
        setAuthSession(null, nextUser);
        return nextUser;
      });
      setChangePwdForm({ current: "", next: "", confirm: "" });
      toast.success("Password updated successfully. Welcome!");
    } catch (err) {
      setChangePwdError(err.response?.data?.detail || "Failed to change password. Please check your current password.");
    } finally {
      setChangePwdLoading(false);
    }
  };

  if (checking) return <div className="loading-screen">Loading workspace…</div>;
  if (!user) {
    return (
      <>
        <Login
          onLogin={(u) => {
            setDeactivationMsg("");
            setAuthSession(u.access_token || null, u);
            setUser(u);
            loadCore();
            toast.success(`Welcome, ${u.name}`);
          }}
          deactivationMsg={deactivationMsg}
        />
        <Toaster richColors position="top-right" />
      </>
    );
  }

  const shellProps = { user, page, setPage: goto, onLogout: logout };

  let content = null;
  if (page === "OrderDetail" && selectedOrderId) {
    content = (
      <OrderDetail
        orderId={selectedOrderId}
        onBack={() => goBack("Orders")}
        onChange={loadCore}
        user={user}
        clients={clients}
      />
    );
  } else if (page === "DesignDetail" && selectedDesignOrderId) {
    content = <DesignDetail orderId={selectedDesignOrderId} onBack={() => goBack("Design")} onChange={loadCore} user={user} />;
  } else if (page === "ProductionDetail" && selectedProductionOrderId) {
    content = <ProductionDetail orderId={selectedProductionOrderId} onBack={() => goBack("Production")} onChange={loadCore} user={user} />;
  } else if (page === "DispatchDetail" && selectedDispatchOrderId) {
    content = <DispatchDetail orderId={selectedDispatchOrderId} onBack={() => goBack("Dispatch")} onChange={loadCore} user={user} />;
  } else if (page === "DeliveryDetail" && selectedDeliveryOrderId) {
    content = (
      <DeliveryDetail
        orderId={selectedDeliveryOrderId}
        onBack={() => {
          if (selectedDeliveryPersonId) {
            openDeliveryPerson(selectedDeliveryPersonId);
          } else {
            goBack("Delivery");
          }
        }}
        onChange={loadCore}
        user={user}
      />
    );
  } else if (page === "DeliveryPersonDetail" && selectedDeliveryPersonId) {
    content = (
      <DeliveryPersonDetail
        personId={selectedDeliveryPersonId}
        user={user}
        onBack={() => goBack("Delivery")}
        onOpenOrder={openOrder}
        onOpenDelivery={openDelivery}
        onChange={loadCore}
      />
    );
  } else if (page === "TaskDetail" && selectedTaskId) {
    content = <TaskDetail taskId={selectedTaskId} user={user} onBack={() => goBack("Tasks & Reports")} onChange={loadCore} />;
  } else if (page === "Dashboard") {
    content = (
      <Dashboard
        data={dashboard}
        orders={orders}
        onOpen={openOrder}
        onNavigate={goto}
        user={user}
        selectedCompany={selectedDashboardCompany}
        onCompanyChange={handleDashboardCompanyChange}
        companies={companiesList}
      />
    );
  } else if (page === "Activities") {
    content = (
      <Activities
        user={user}
        onOpen={openOrder}
        onNavigate={goto}
        openDesign={openDesign}
        openProduction={openProduction}
        openDispatch={openDispatch}
        openDelivery={openDelivery}
        openClient={openClient}
        openLead={openLead}
        openProcurement={openProcurement}
        routeParams={routeParams}
      />
    );
  } else if (page === "Tasks & Reports" || page === "Tasks") {
    content = <TasksReports user={user} onOpenTask={openTask} />;
  } else if (page === "ClientOrders" && selectedClientId) {
    content = (
      <ClientOrders
        clientId={selectedClientId}
        orders={orders}
        clients={clients}
        user={user}
        onBack={() => goBack("Orders")}
        onOpenOrder={openOrder}
        onChange={loadCore}
      />
    );
  } else if (page === "Orders") {
    content = (
      <Orders
        orders={orders}
        clients={clients}
        onOpenClientOrders={openClientOrders}
        onOpen={openOrder}
        onCreated={loadCore}
        user={user}
        routeParams={routeParams}
        onNavigate={goto}
      />
    );
  } else if (page === "Clients") {
    content = <Clients clients={clients} onOpen={openOrder} onChange={loadCore} user={user} onSelectClient={openClient} />;
  } else if (page === "ClientDetail" && selectedClientId) {
    content = <ClientDetail clientId={selectedClientId} user={user} onBack={() => goBack("Clients")} onOpenOrder={openOrder} />;
  } else if (page === "Leads") {
    content = <Leads user={user} onOpenOrder={openOrder} selectedLeadId={selectedLeadId} onOpenLead={openLead} />;
  } else if (page === "LeadDetail" && selectedLeadId) {
    content = <LeadDetail leadId={selectedLeadId} user={user} onBack={() => goBack("Leads")} onOpenOrder={openOrder} onOpenProcurement={openProcurement} />;
  } else if (page === "Procurement") {
    content = <Procurement user={user} onOpenLead={openLead} onOpenClient={openClient} onNavigate={navigateTo} onOpenRequest={openProcurement} />;
  } else if (page === "ProcurementDetail" && selectedRequestId) {
    content = (
      <ProcurementDetail
        requestId={selectedRequestId}
        user={user}
        onBack={() => goBack("Procurement")}
        onOpenLead={openLead}
        onOpenClient={openClient}
        onChange={loadCore}
      />
    );
  } else if (page === "Design") {
    content = <Design orders={orders} clients={clients} onOpen={openOrder} onOpenDesign={openDesign} onChange={loadCore} user={user} />;
  } else if (page === "Approvals") {
    content = <Approvals orders={orders} clients={clients} onOpen={openOrder} onOpenDesign={openDesign} onChange={loadCore} user={user} routeParams={routeParams} />;
  } else if (page === "Production") {
    content = <Production orders={orders} onOpen={openProduction} user={user} onChange={loadCore} routeParams={routeParams} />;
  } else if (page === "Dispatch" || page === "Challans") {
    content = <Dispatch orders={orders} onOpen={openDispatch} user={user} onChange={loadCore} routeParams={routeParams} />;
  } else if (page === "Delivery") {
    content = <Delivery user={user} onOpen={openDelivery} onOpenDeliveryPerson={openDeliveryPerson} onChange={loadCore} routeParams={routeParams} />;
  } else if (page === "ClientAccounts" && selectedClientId) {
    content = (
      <ClientAccounts
        clientId={selectedClientId}
        user={user}
        onBack={() => goBack("Accounts")}
        onOpenOrder={openOrder}
      />
    );
  } else if (page === "Accounts" || page === "Invoices" || page === "Payments") {
    content = (
      <Accounts
        user={user}
        onOpenClientAccounts={openClientAccounts}
        onOpenOrder={openOrder}
        onNavigate={goto}
      />
    );
  } else if (page === "Reports") {
    content = <Reports user={user} />;
  } else if (page === "Users & Roles") {
    content = <UsersRoles user={user} onOpenUser={openUser} />;
  } else if (page === "UserDetail" && selectedUserId) {
    content = (
      <UserDetail
        userId={selectedUserId}
        onBack={() => goBack("Users & Roles")}
        currentUser={user}
        onUserUpdated={loadCore}
      />
    );
  } else if (page === "Settings") {
    content = isSuperAdmin(user) ? <Settings user={user} /> : null;
  } else if (page === "Profile") {
    content = (
      <Profile
        user={user}
        onUserUpdated={(updatedUser) => {
          setUser((prev) => {
            const next = { ...prev, ...updatedUser };
            setAuthSession(null, next);
            return next;
          });
        }}
        onNavigate={goto}
      />
    );
  }

  return (
    <>
      <Shell {...shellProps} onOpenOrder={openOrder} onOpenClient={openClient} onOpenLead={openLead} onNavigate={goto}>{content}</Shell>
      <TaskReminderWidget user={user} onOpenTask={openTask} />
      <Toaster richColors position="top-right" />

      {/* Forced password change modal â€” shown when must_change_password is true */}
      {user?.must_change_password && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 99999,
            background: "rgba(15,23,42,0.72)",
            display: "flex", alignItems: "center", justifyContent: "center",
            backdropFilter: "blur(3px)",
          }}
        >
          <form
            ref={changePwdRef}
            onSubmit={handleForcedPasswordChange}
            style={{
              background: "#fff", borderRadius: 12, width: "min(420px, 95%)",
              boxShadow: "0 8px 32px rgba(15,23,42,0.18)", overflow: "hidden",
            }}
          >
            {/* Header */}
            <div style={{ background: "#0f172a", padding: "22px 28px" }}>
              <div style={{ color: "#fcd34d", fontSize: 11, fontWeight: 700, letterSpacing: 1.5, marginBottom: 4 }}>SECURITY NOTICE</div>
              <div style={{ color: "#fff", fontSize: 18, fontWeight: 700 }}>Password Change Required</div>
              <div style={{ color: "#94a3b8", fontSize: 12, marginTop: 4 }}>Your account is using a temporary password. Please set a permanent password to continue.</div>
            </div>

            {/* Body */}
            <div style={{ padding: "24px 28px" }}>
              <div className="dispatch-field">
                <label className="dispatch-label">Current Temporary Password</label>
                <PasswordInput
                  className="dispatch-control"
                  placeholder="Enter the temporary password you received"
                  value={changePwdForm.current}
                  onChange={(e) => setChangePwdForm((f) => ({ ...f, current: e.target.value }))}
                  required
                  autoFocus
                  data-testid="force-change-current-password-input"
                />
              </div>
              <div className="dispatch-field">
                <label className="dispatch-label">New Password</label>
                <PasswordInput
                  className="dispatch-control"
                  placeholder="At least 6 characters"
                  value={changePwdForm.next}
                  onChange={(e) => setChangePwdForm((f) => ({ ...f, next: e.target.value }))}
                  required
                  minLength={6}
                  data-testid="force-change-new-password-input"
                />
              </div>
              <div className="dispatch-field">
                <label className="dispatch-label">Confirm New Password</label>
                <PasswordInput
                  className="dispatch-control"
                  placeholder="Repeat new password"
                  value={changePwdForm.confirm}
                  onChange={(e) => setChangePwdForm((f) => ({ ...f, confirm: e.target.value }))}
                  required
                  data-testid="force-change-confirm-password-input"
                />
              </div>

              {changePwdError && (
                <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 6, padding: "10px 12px", fontSize: 12, color: "#b91c1c", marginBottom: 4 }}>
                  {changePwdError}
                </div>
              )}

              <button
                type="submit"
                className="primary-btn"
                style={{ width: "100%", marginTop: 8, justifyContent: "center" }}
                disabled={changePwdLoading}
              >
                {changePwdLoading ? "Updatingâ€¦" : "Set Permanent Password"}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

